// apps/client/src/hooks/pipelineTracker/reportCache.ts
//
// Session cache for the tracker's read callables.
//
// WHY: every tracker report is computed from a committed snapshot, and a committed snapshot does
// not change. Before this, each tab mounted its hooks afresh and asked the server again, so
// switching to Operations or the Board pack and back cost the full server time (10 to 20 seconds
// on stage for the demo book) every time. An answer for the same tenant and the same request is
// now kept for the session and shown at once.
//
// FRESHNESS: an answer can only go stale when the tenant writes something the reports read: a new
// snapshot, a stage or task mapping, the roster, targets, monthly inputs, REM settings or ad
// spend. Every one of those writes calls invalidateTrackerReports(), so the next read asks the
// server. As a backstop for a write made elsewhere (another person, another browser), an answer
// older than REPORT_FRESH_MS is still shown at once but re-asked in the background.
//
// SAFETY: held in the app's one QueryClient, so the auth-boundary rule in useQueryCacheLifecycle
// (clear everything when the signed-in user changes) applies here too. Keys also carry the uid
// and the companyId, as defence in depth. Errors are never cached.

import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../lib/firebase';
import { queryClient } from '../../lib/queryClient';

export const TRACKER_QUERY_PREFIX = 'pipelineTracker';

/** How long an answer is served without asking again. Snapshots are weekly; this is generous. */
export const REPORT_FRESH_MS = 10 * 60 * 1000;

/** How long an unused answer is kept in memory. */
const REPORT_GC_MS = 30 * 60 * 1000;

export type TrackerRequest = { action: string; companyId: string } & Record<string, unknown>;

/** Field order must not split one request into two cache entries; undefined fields are dropped. */
function stableRequest(request: TrackerRequest): string {
  const entries = Object.entries(request)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return JSON.stringify(entries);
}

function currentUid(): string | null {
  return (auth as { currentUser?: { uid?: string } | null })?.currentUser?.uid ?? null;
}

export function trackerQueryKey(callable: string, request: TrackerRequest) {
  return [
    TRACKER_QUERY_PREFIX,
    currentUid(),
    request.companyId,
    callable,
    request.action,
    stableRequest(request),
  ] as const;
}

/** The key prefix shared by every request for one action, whatever its other fields. */
export function trackerActionKey(callable: string, companyId: string, action: string) {
  return [TRACKER_QUERY_PREFIX, currentUid(), companyId, callable, action] as const;
}

/** The cached answer for a request, if any, and whether it is still inside the fresh window. */
export function peekTrackerCall<T>(
  callable: string,
  request: TrackerRequest
): { data: T; fresh: boolean } | undefined {
  const state = queryClient.getQueryState<T>(trackerQueryKey(callable, request));
  if (!state || state.data === undefined) return undefined;
  return {
    data: state.data,
    fresh: !state.isInvalidated && Date.now() - state.dataUpdatedAt < REPORT_FRESH_MS,
  };
}

/**
 * Ask a tracker callable through the cache.
 *
 * A fresh cached answer resolves without a request, and two callers asking the same thing at the
 * same time share one request. `force` always asks the server (the Retry buttons).
 */
export function fetchTrackerCall<T>(
  callable: string,
  request: TrackerRequest,
  options: { force?: boolean } = {}
): Promise<T> {
  return queryClient.fetchQuery({
    queryKey: trackerQueryKey(callable, request),
    queryFn: async () => (await httpsCallable<TrackerRequest, T>(functions, callable)(request)).data,
    staleTime: options.force ? 0 : REPORT_FRESH_MS,
    gcTime: REPORT_GC_MS,
  });
}

/** Warm the cache without surfacing anything. A failure is left for the tab to meet and report. */
export function prefetchTrackerCall(callable: string, request: TrackerRequest): void {
  fetchTrackerCall(callable, request).catch(() => undefined);
}

/**
 * Drop every cached tracker answer. Called after any write a report reads, so nothing computed
 * before the write is shown after it.
 */
export function invalidateTrackerReports(): void {
  queryClient.removeQueries({ queryKey: [TRACKER_QUERY_PREFIX] });
}

export interface TrackerCallState<T> {
  /**
   * The answer for the current request; while a different request is loading, the last answer
   * (the hooks this replaced behaved the same way). `dataKey` says which request it answered.
   */
  data: T | null;
  dataKey: string;
  /** The current request's key. `dataKey === key` means `data` answers what is on screen. */
  key: string;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * One cached tracker call as hook state.
 *
 * `request` null means "do not ask" (no tenant yet, or the caller has disabled the read).
 * A cached answer for the request is returned on the FIRST render, so a tab opened a second time
 * draws its figures immediately instead of a skeleton.
 */
export function useTrackerCall<T>(
  callable: string,
  request: TrackerRequest | null,
  options: { label: string; describeError: (err: unknown) => string }
): TrackerCallState<T> {
  const key = request ? `${callable}|${stableRequest(request)}` : '';
  const [answer, setAnswer] = useState<{ key: string; data: T | null; error: string | null }>(() => {
    const hit = request ? peekTrackerCall<T>(callable, request) : undefined;
    return { key: hit ? key : '', data: hit ? hit.data : null, error: null };
  });
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const forceRef = useRef(false);
  const requestRef = useRef(request);
  requestRef.current = request;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const refetch = useCallback(() => {
    forceRef.current = true;
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const current = requestRef.current;
    if (!current) {
      setPendingKey(null);
      return;
    }
    let cancelled = false;
    const force = forceRef.current;
    forceRef.current = false;

    const hit = peekTrackerCall<T>(callable, current);
    if (hit) setAnswer({ key, data: hit.data, error: null });
    if (hit && hit.fresh && !force) {
      setPendingKey(null);
      return;
    }
    // A first load or a Retry shows loading. An aged answer stays on screen while it is re-asked.
    const quiet = Boolean(hit) && !force;
    setPendingKey(quiet ? null : key);
    if (!quiet) setAnswer((prev) => (prev.key === key ? { ...prev, error: null } : prev));

    fetchTrackerCall<T>(callable, current, { force })
      .then((data) => {
        if (!cancelled) setAnswer({ key, data, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        // Surfaced, never swallowed. A quiet re-ask that fails keeps the answer already shown.
        console.error(`[${optionsRef.current.label}] request failed:`, err);
        if (!quiet) setAnswer({ key, data: null, error: optionsRef.current.describeError(err) });
      })
      .finally(() => {
        if (!cancelled) setPendingKey((p) => (p === key ? null : p));
      });

    return () => {
      cancelled = true;
    };
  }, [callable, key, nonce]);

  // Render-time read, so a cached answer is drawn on the very frame the request changes rather
  // than one effect later.
  const hit = request && answer.key !== key ? peekTrackerCall<T>(callable, request) : undefined;
  const settled = answer.key === key || Boolean(hit);
  return {
    data: answer.key === key ? answer.data : hit ? hit.data : answer.data,
    dataKey: answer.key === key || !hit ? answer.key : key,
    key,
    loading: request !== null && (pendingKey === key || !settled),
    error: answer.key === key ? answer.error : null,
    refetch,
  };
}
